/* @flow */
import idx from 'idx';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { FormattedNumber } from 'react-intl';
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import ContentLoader from 'react-content-loader';
import DeliveryModalSection from 'chat/components/DeliveryModalSection.jsx';
import Dialog from 'components/Dialog.jsx';
import EventTracking from 'lib/EventTracking';
import ScrollableArea from 'components/ScrollableArea.jsx';
import TitleHeader from 'components/TitleHeader.jsx';

import './styles/DeliveryModal.scss';

const EVENT_KEYS = {
  SUMMARY_VIEW: 'delivery_summary.view',
  NOT_YET_LOADED_VIEW: 'delivery_summary.not_yet_loaded.view',
  READ_USER_UNFOLD: 'delivery_summary.precise_summary.read_users.click',
  ERRORED_USER_UNFOLD: 'delivery_summary.precise_summary.errored_users.click',
  UNREACHABLE_USER_UNFOLD: 'delivery_summary.precise_summary.unreachable_users.click',
  DELIVERED_USER_UNFOLD: 'delivery_summary.precise_summary.delivered_users.click',
  ERRORED_USER_MAILTO: 'delivery_summary.estimated_summary.errored_users.contact_support.click',
};

type Props = {
  loading: boolean,
  onRequestHide: Function,
  messageReadSummaries?: ?*,
  onRefetch?: () => void,
  onRequestDeliveryFailures?: () => void,
  onRequestDeliveryUnreachables?: () => void,
};

// prettier-ignore
const Ghost = () => (
  <ContentLoader height={500} speed={1}>
    <rect id="svg_26" height="20.00002" width="148.38723" y="32.25794" x="130.99993" stroke="#fff" fill="#000" />
    <rect id="svg_27" height="12.25808" width="96.77428" y="63.87087" x="156.8064" stroke="#fff" fill="#000" />
    <rect id="svg_28" height="12" width="209.67761" y="127.09673" x="32" stroke="#fff" fill="#000" />
    <rect id="svg_29" height="10.32259" width="49.67747" y="104.51607" x="32" stroke="#fff" fill="#000" />
    <rect stroke="#fff" id="svg_30" height="1" width="339" y="156.77418" x="32" fill="#000" />
    <rect id="svg_31" height="15.48389" width="33.54842" y="114.1935" x="336.80657" stroke="#fff" fill="#000" />
    <ellipse ry="9.35485" rx="9.35485" id="svg_32" cy="121.61286" cx="320.35494" stroke="#fff" fill="#000" />
    <rect id="svg_33" height="12" width="209.67761" y="197.41938" x="32" stroke="#fff" fill="#000" />
    <rect id="svg_34" height="10.32259" width="49.67747" y="174.83871" x="32" stroke="#fff" fill="#000" />
    <rect stroke="#fff" id="svg_35" height="1" width="339" y="227.09683" x="32" fill="#000" />
    <rect id="svg_36" height="15.48389" width="33.54842" y="184.51614" x="336.80657" stroke="#fff" fill="#000" />
    <ellipse ry="9.35485" rx="9.35485" id="svg_37" cy="191.9355" cx="320.35494" stroke="#fff" fill="#000" />
    <rect id="svg_38" height="12" width="209.67761" y="269.67751" x="32" stroke="#fff" fill="#000" />
    <rect id="svg_39" height="10.32259" width="49.67747" y="247.09684" x="32" stroke="#fff" fill="#000" />
    <rect stroke="#fff" id="svg_40" height="1" width="339" y="299.35495" x="32" fill="#000" />
    <rect id="svg_41" height="15.48389" width="33.54842" y="256.77427" x="336.80657" stroke="#fff" fill="#000" />
    <ellipse ry="9.35485" rx="9.35485" id="svg_42" cy="264.19363" cx="320.35494" stroke="#fff" fill="#000" />
  </ContentLoader>
);

export class DeliveryModal extends React.Component<Props, void> {
  componentDidUpdate(prevProps: Props) {
    if (!prevProps.messageReadSummaries && this.props.messageReadSummaries) {
      EventTracking.trackKey(EVENT_KEYS.SUMMARY_VIEW);
    } else if (prevProps.loading && !this.props.loading && !this.props.messageReadSummaries) {
      EventTracking.trackKey(EVENT_KEYS.NOT_YET_LOADED_VIEW);
    }
  }

  ScrollableArea: ScrollableArea;
  ReadSection: DeliveryModalSection;
  DeliveredSection: DeliveryModalSection;
  DeliveryErrorSection: DeliveryModalSection;
  UnreachableSection: DeliveryModalSection;

  computePercentageOfRecipients(count: number): number {
    return Math.floor((count / this.getRecipientCount()) * 100);
  }

  getRecipientCount(): number {
    return idx(this.props, _ => _.messageReadSummaries.recipientCount) || 0;
  }

  isEstimate(summary: *): boolean {
    return summary.__typename === 'EstimatedSummaryEntry';
  }

  scrollToSection = (section: DeliveryModalSection) => {
    const node = ReactDOM.findDOMNode(section);
    if (!node) {
      console.error('DeliveryModal - Tried to scroll to nonexistent section');
      return;
    }
    if (!this.ScrollableArea) {
      console.error('DeliveryModal - Tried to scroll without a ScrollableArea');
      return;
    }
    this.ScrollableArea.scrollToTopOfElement(node);
  };

  getReadSection() {
    const summary = idx(this.props, _ => _.messageReadSummaries.readEntry) || {};

    return (
      <DeliveryModalSection
        ref={(a: any) => (this.ReadSection = a)}
        title="read"
        status="read"
        users={summary.__typename === 'PreciseSummaryEntry' ? summary.users : undefined}
        count={summary.count}
        isEstimate={this.isEstimate(summary)}
        percent={this.computePercentageOfRecipients(summary.count)}
        onSectionUnfold={() => {
          EventTracking.trackKey(EVENT_KEYS.READ_USER_UNFOLD);
          this.scrollToSection(this.ReadSection);
        }}
      />
    );
  }

  getUnreadSection() {
    const summary = idx(this.props, _ => _.messageReadSummaries.deliveredEntry) || {};

    return (
      <DeliveryModalSection
        ref={(a: any) => (this.DeliveredSection = a)}
        title="delivered"
        status="delivered"
        users={summary.__typename === 'PreciseSummaryEntry' ? summary.users : undefined}
        count={summary.count}
        isEstimate={this.isEstimate(summary)}
        percent={this.computePercentageOfRecipients(summary.count)}
        onSectionUnfold={() => {
          EventTracking.trackKey(EVENT_KEYS.DELIVERED_USER_UNFOLD);
          this.scrollToSection(this.DeliveredSection);
        }}
      />
    );
  }

  getErrorSection() {
    const summary = idx(this.props, _ => _.messageReadSummaries.failedEntry) || {};
    const downloadAvailable = idx(this.props, _ => _.messageReadSummaries.downloadAvailable);
    const downloadAvailableUntil = idx(
      this.props,
      _ => _.messageReadSummaries.downloadAvailableUntil
    );

    return (
      <DeliveryModalSection
        ref={(a: any) => (this.DeliveryErrorSection = a)}
        title="error"
        status="delivery_error"
        users={summary.__typename === 'PreciseSummaryEntry' ? summary.users : undefined}
        count={summary.count}
        isEstimate={this.isEstimate(summary)}
        percent={this.computePercentageOfRecipients(summary.count)}
        onSectionUnfold={() => {
          EventTracking.trackKey(EVENT_KEYS.ERRORED_USER_UNFOLD);
          this.scrollToSection(this.DeliveryErrorSection);
        }}
        onRequestCSV={this.props.onRequestDeliveryFailures}
        downloadAvailable={downloadAvailable}
        downloadAvailableUntil={downloadAvailableUntil}
      />
    );
  }

  getUnreachableSection() {
    const summary = idx(this.props, _ => _.messageReadSummaries.unreachableEntry) || {};
    const downloadAvailable = idx(this.props, _ => _.messageReadSummaries.downloadAvailable);
    const downloadAvailableUntil = idx(
      this.props,
      _ => _.messageReadSummaries.downloadAvailableUntil
    );

    if (summary.count < 1) {
      return null;
    }

    return (
      <DeliveryModalSection
        ref={(a: any) => (this.UnreachableSection = a)}
        title="no contact information"
        status="unreachable"
        users={summary.__typename === 'PreciseSummaryEntry' ? summary.users : undefined}
        count={summary.count}
        isEstimate={this.isEstimate(summary)}
        onSectionUnfold={() => {
          EventTracking.trackKey(EVENT_KEYS.UNREACHABLE_USER_UNFOLD);
          this.scrollToSection(this.UnreachableSection);
        }}
        onRequestCSV={this.props.onRequestDeliveryUnreachables}
        downloadAvailable={downloadAvailable}
        downloadAvailableUntil={downloadAvailableUntil}
      />
    );
  }

  render() {
    if (this.props.loading) {
      return (
        <Dialog
          size="small"
          className="DeliveryModal DeliveryModal-loading"
          onRequestHide={this.props.onRequestHide}>
          <Ghost />
        </Dialog>
      );
    }

    if (!this.props.messageReadSummaries) {
      return (
        <Dialog
          size="small"
          className="DeliveryModal DeliveryModal-error"
          onRequestHide={this.props.onRequestHide}>
          <BoxBody>
            <TitleHeader
              flush
              image={require('assets/images/admin/desk.svg')}
              imageSize="xxsmall"
              title="We’re creating your message summary."
              subtext="Please try again in a moment to see reads, deliveries, and errors for your message."
              action={
                this.props.onRefetch ? (
                  <Button onClick={this.props.onRefetch}>Try again</Button>
                ) : null
              }
            />
          </BoxBody>
        </Dialog>
      );
    }

    return (
      <Dialog
        size="small"
        title="Message summary"
        className="DeliveryModal"
        onRequestHide={this.props.onRequestHide}
        subtext={
          this.getRecipientCount() === 1 ? (
            `Sent to ${this.getRecipientCount()} person`
          ) : (
            <span>
              Sent to <FormattedNumber value={this.getRecipientCount()} /> people
            </span>
          )
        }>
        <BoxBody>
          <ScrollableArea
            ref={(a: any) => (this.ScrollableArea = a)}
            className="DeliveryModal-sections">
            {this.getReadSection()}
            {this.getUnreadSection()}
            {this.getErrorSection()}
            {this.getUnreachableSection()}
          </ScrollableArea>
        </BoxBody>
      </Dialog>
    );
  }
}

export default DeliveryModal;
